/**
 * use-dashboard — 数据总览导出 API (ADM-DASH-S010)
 *
 * 契约源：dev-docs 02-API契约/A0-运营总览.md §2 报表导出（POST /export 同步生成 →
 * GET /exports 下载中心列表 → GET /exports/:id/download 认证下载代理）。
 *
 * - export / listExports 走统一 apiRequest（JSON envelope 解包 + JWT 注入）。
 * - download 走 downloadExportFile：下载端点回的是**裸 CSV 流**（Content-Type text/csv +
 *   Content-Disposition attachment），非 { code, data } envelope → 不能用 apiRequest（它强解 JSON
 *   envelope）。这里手写 fetch + JWT 注入，取 blob 触发浏览器下载；非 2xx 时按 envelope 解出
 *   错误码抛 AdminApiError，让调用方走统一错误 toast（不假成功）。
 */

import type { DashboardExportRecord, DashboardExportRequest } from '@novel-admin/shared';
import { apiRequest } from '~/api/client';
import { AdminApiError, type AdminErrorBody } from '~/api/errors';
import { authedFetch } from '~/api/session-refresh';
import { newRequestId } from '~/lib/request-id';

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

/** 从 Content-Disposition 解析下载文件名；缺失时回退到 dashboard_<range>.csv 形态由调用方兜底 */
function filenameFromDisposition(disposition: string | null): string | null {
  if (!disposition) return null;
  const m = /filename="?([^"]+)"?/.exec(disposition);
  return m?.[1] ?? null;
}

export const dashboardApi = {
  /** POST /api/v1/dashboard/export — 提交导出（同步生成，提交即返终态记录）*/
  export: (body: DashboardExportRequest) =>
    apiRequest<DashboardExportRecord>({
      method: 'POST',
      path: '/api/v1/dashboard/export',
      body,
    }),

  /** GET /api/v1/dashboard/exports — 下载中心列表（created_at 倒序，运营共享视图）*/
  listExports: () =>
    apiRequest<{ items: DashboardExportRecord[] }>({
      method: 'GET',
      path: '/api/v1/dashboard/exports',
    }),

  /**
   * GET /api/v1/dashboard/exports/:id/download — 认证下载代理（裸 CSV 流）。
   * 报表含财务数据 → 走 admin RBAC 门禁端点流式回 R2，不用签名 URL。
   * 成功：取 blob → a[download] 触发浏览器保存（fallbackName 用于无 Content-Disposition 时）。
   * 失败：按 envelope 解出业务码抛 AdminApiError（调用方统一错误 toast）。
   */
  download: async (id: string, fallbackName: string): Promise<void> => {
    const url = `${API_BASE || ''}/api/v1/dashboard/exports/${id}/download`;
    // 鉴权注入 + access 过期(401) 自动 refresh+重试一次 交给 authedFetch；最终仍 401 才在下方 clearSession
    const res = await authedFetch(url, {
      method: 'GET',
      headers: { 'X-Request-Id': newRequestId() },
    });

    if (!res.ok) {
      // 非 2xx：错误响应是 JSON envelope（{ code, message, requestId }）→ 解出业务码抛
      let body: AdminErrorBody | null = null;
      try {
        body = (await res.json()) as AdminErrorBody;
      } catch {
        body = null;
      }
      // 401 登出由 authedFetch 统一处理（仅 refresh 显式失效才 forceLogout）；此处只抛错
      throw new AdminApiError(
        res.status,
        body ?? {
          code: 'INTERNAL_ERROR',
          message: `Download failed: ${res.status}`,
          requestId: '',
        },
      );
    }

    const blob = await res.blob();
    const filename =
      filenameFromDisposition(res.headers.get('Content-Disposition')) ?? fallbackName;
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
  },
};
